import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type MerchItem } from "../api";
import { Card, SectionHeading, Spinner, Tag } from "./ui";

type Status = "cached" | "generating" | "empty";

interface CachedResponse {
  content: { type: "merch"; data: MerchItem[]; generatedAt: string } | null;
  status: Status;
}

interface Props {
  bookId: number;
}

const TYPE_LABELS: Record<string, string> = {
  special_edition: "Special edition",
  signed_copy: "Signed copy",
  apparel: "Apparel",
  tote: "Tote",
  mug: "Mug",
  poster: "Poster",
  art: "Art print",
  bookmark: "Bookmark",
  other: "Other",
};

// Designed to be silent when there is nothing to show. This component handles
// its own visibility so the rest of the page lays out cleanly with or without
// it. See BookSections.tsx — we just drop <MerchSection /> in and trust it.
export function MerchSection({ bookId }: Props) {
  const qc = useQueryClient();

  const cache = useQuery({
    queryKey: ["ai", bookId, "merch"],
    queryFn: () => api.get<CachedResponse>(`/ai/${bookId}/merch`),
    refetchInterval: (query) => {
      const d = query.state.data as CachedResponse | undefined;
      if (!d) return 4_000;
      return d.status === "generating" ? 4_000 : false;
    },
  });

  const regenerate = useMutation({
    mutationFn: () => api.post(`/ai/${bookId}/merch/generate?force=1`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai", bookId, "merch"] }),
  });

  const status = cache.data?.status ?? "empty";
  const items = cache.data?.content?.data ?? [];
  const hasItems = items.length > 0;

  // Hide entirely when:
  // - First load is still pending (no cache state yet)
  // - Cache state is 'cached' but the list is empty (Claude found nothing)
  // - Cache state is 'empty' (nothing's been checked yet — typical for older books)
  if (cache.isLoading) return null;
  if (status === "cached" && !hasItems) return null;
  if (status === "empty") return null;

  // Generating but no cache yet → show a slim placeholder so the user knows
  // a check is in flight. This dissolves to either content or hidden state.
  if (status === "generating" && !hasItems) {
    return (
      <Card>
        <div className="flex items-center gap-3 text-plum-soft">
          <Spinner size={16} />
          <span className="text-sm italic">Checking the internet for merch related to this book…</span>
        </div>
      </Card>
    );
  }

  return (
    <Card accent>
      <div className="flex items-start justify-between gap-4 mb-6">
        <SectionHeading
          eyebrow="Take a piece home"
          title="Merch worth knowing about"
          description="Things readers of this book may want to own. Verify links and availability before buying — these are AI suggestions."
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((m, i) => (
          <article
            key={`${m.name}-${i}`}
            className="flex flex-col gap-2.5 p-5 rounded-xl bg-cream-50/70 border border-cream-200"
          >
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-serif text-lg text-plum leading-tight">{m.name}</h3>
              <Tag>{TYPE_LABELS[m.type] ?? m.type}</Tag>
            </div>
            <p className="text-plum-soft text-sm leading-relaxed">{m.description}</p>
            <div className="flex items-center justify-between gap-3 pt-2 mt-auto border-t border-cream-200">
              <span className="text-xs italic text-plum-mute">via {m.vendor}</span>
              {m.url ? (
                <a
                  href={m.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-xs uppercase tracking-[0.14em] text-rose hover:text-rose-dark"
                >
                  View →
                </a>
              ) : null}
            </div>
          </article>
        ))}
      </div>

      <div className="mt-6 pt-4 border-t border-cream-200 flex items-center justify-between gap-3">
        <p className="text-xs text-plum-mute italic">
          Always verify before purchasing — availability changes and AI suggestions can drift.
        </p>
        <button
          onClick={() => regenerate.mutate()}
          disabled={regenerate.isPending}
          className="text-[0.65rem] uppercase tracking-[0.16em] text-plum-mute hover:text-rose disabled:opacity-50"
        >
          {regenerate.isPending ? "Refreshing…" : "Refresh list"}
        </button>
      </div>
    </Card>
  );
}
